// src/pages/admin/AdminAnnouncementPage.tsx
// 一般ユーザー向けお知らせバナーの編集画面。
// GET相当はoperation_type: 'get_announcement'、更新は'set_announcement'。
// 保存すると即座に AnnouncementBanner（一般ユーザー側、認証不要のGET /announcement）に反映される。

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useAuth } from '../../context/AuthContext';
import { useAppTokens } from '../../context/ThemeModeContext';
import { NEW_API } from '../../config/api';
import AdminNav from './AdminNav';

const MAX_LENGTH = 500;

const AdminAnnouncementPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { idToken } = useAuth();

  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'info' | 'warning'>('info');
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const cardSx = {
    border: `1px solid ${TOKENS.border}`,
    background: TOKENS.glass,
    borderRadius: '18px',
    p: 2.5,
  } as const;

  const inputSx = {
    '& .MuiOutlinedInput-root': { color: TOKENS.text, background: TOKENS.buttonBg, borderRadius: '12px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
    '& .MuiInputLabel-root': { color: TOKENS.textSub },
  } as const;

  const load = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    try {
      const res = await axios.post(
        NEW_API.ADMIN,
        { operation_type: 'get_announcement' },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      const data = res.data?.data ?? {};
      setMessage(data.message ?? '');
      setSeverity(data.severity === 'warning' ? 'warning' : 'info');
      setIsActive(Boolean(data.is_active));
      setUpdatedAt(data.updated_at ?? null);
    } catch (e) {
      setSnackbar({ open: true, message: 'お知らせの取得に失敗しました', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!idToken) return;
    setSaving(true);
    try {
      const res = await axios.post(
        NEW_API.ADMIN,
        { operation_type: 'set_announcement', message, severity, is_active: isActive },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      setUpdatedAt(res.data?.data?.updated_at ?? null);
      setSnackbar({ open: true, message: '保存しました', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.response?.data?.error?.message || '保存に失敗しました', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100dvh', background: TOKENS.bg, color: TOKENS.text }}>
      <AdminNav />
      <Box sx={{ maxWidth: 720, margin: 'auto', px: 2, py: 4 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 0.5 }}>お知らせ管理</Typography>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 3 }}>
          ここで設定した内容は、有効化するとReadLinkerの全ユーザーに表示されます。
        </Typography>

        {loading ? (
          <CircularProgress size={22} sx={{ color: TOKENS.text }} />
        ) : (
          <Box sx={cardSx}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="お知らせ本文"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
              helperText={`${message.length} / ${MAX_LENGTH}文字`}
              sx={{ ...inputSx, mb: 2.5 }}
            />

            <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 1 }}>種別</Typography>
            <ToggleButtonGroup
              exclusive
              value={severity}
              onChange={(_, v) => v && setSeverity(v)}
              sx={{ mb: 2.5 }}
            >
              <ToggleButton value="info" sx={{ textTransform: 'none', color: TOKENS.text }}>
                お知らせ（info）
              </ToggleButton>
              <ToggleButton value="warning" sx={{ textTransform: 'none', color: TOKENS.text }}>
                注意喚起（warning）
              </ToggleButton>
            </ToggleButtonGroup>

            <FormControlLabel
              sx={{ display: 'block', mb: 2.5 }}
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label={
                <Typography sx={{ fontSize: 13.5, color: TOKENS.text }}>
                  {isActive ? '表示中（一般ユーザーに見えています）' : '非表示'}
                </Typography>
              }
            />

            {updatedAt && (
              <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub, mb: 2 }}>
                最終更新: {new Date(updatedAt).toLocaleString('ja-JP')}
              </Typography>
            )}

            <Button
              onClick={handleSave}
              disabled={saving}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                color: TOKENS.bg,
                background: TOKENS.text,
                '&:hover': { background: TOKENS.textSub },
              }}
            >
              {saving ? <CircularProgress size={16} sx={{ color: TOKENS.bg }} /> : '保存する'}
            </Button>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminAnnouncementPage;
